import { styled, keyframes } from '@mui/material/styles'
import InputBase from '@mui/material/InputBase'
import { Box } from '@mui/material'

/* ── Animations ── */
const float = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(30px, -40px) scale(1.05); }
  50% { transform: translate(-20px, 20px) scale(0.95); }
  75% { transform: translate(15px, 30px) scale(1.02); }
`

const hexPulse = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.08); }
`

const pulseSweep = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`

const dotPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.4; }
  50% { transform: scale(2); opacity: 1; }
`

const dnaSpin = keyframes`
  0%, 100% { transform: translateX(0); opacity: 0.4; }
  25% { transform: translateX(15px); opacity: 1; }
  50% { transform: translateX(0); opacity: 0.4; }
  75% { transform: translateX(-15px); opacity: 1; }
`

/* ── Layout ── */
export const LoginContainer = styled('div')(() => ({
  display: 'flex',
  height: '100vh',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
}))

export const Main = styled('main')(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '2rem',
  background: 'linear-gradient(145deg, #ffffff 0%, #f0f7fa 100%)',
  position: 'relative',
  zIndex: 2,
  '&::before': {
    content: '""',
    position: 'absolute',
    width: 400,
    height: 400,
    background: 'radial-gradient(circle, rgba(26, 150, 184, 0.06) 0%, transparent 70%)',
    borderRadius: '50%',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
  },
  '@media (max-width: 900px)': {
    background: 'linear-gradient(160deg, #0a2540 0%, #0d3b66 40%, #115e6e 100%)',
  },
}))

export const WelcomeBox = styled(Box)(() => ({
  width: '100%',
  maxWidth: 420,
  background: 'rgba(255, 255, 255, 0.75)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.6)',
  borderRadius: 20,
  padding: '2.5rem 2rem',
  boxShadow: '0 8px 32px rgba(13, 59, 102, 0.08), 0 2px 8px rgba(13, 59, 102, 0.04)',
  '@media (max-width: 900px)': {
    background: 'rgba(255, 255, 255, 0.95)',
  },
}))

export const LogoArea = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: '2rem',
  '@media (max-width: 900px)': {
    '& .logo-text': { color: '#fff' },
    '& .logo-subtitle': { color: 'rgba(255,255,255,0.5)' },
  },
}))

export const LogoText = styled('span')(() => ({
  fontSize: '1.75rem',
  fontWeight: 700,
  color: '#0d3b66',
  letterSpacing: 2,
}))

export const LogoSubtitle = styled('span')(() => ({
  fontSize: '0.8rem',
  fontWeight: 400,
  color: '#7a9bb5',
  letterSpacing: 3,
  textTransform: 'uppercase' as const,
  marginTop: 4,
}))

export const FormTitle = styled('h2')(() => ({
  fontSize: '1.4rem',
  fontWeight: 600,
  color: '#0d3b66',
  marginBottom: 4,
}))

export const FormSubtitle = styled('p')(() => ({
  fontSize: '0.85rem',
  color: '#7a9bb5',
  marginBottom: '1.75rem',
}))

/* ── Input ── */
export const InputGroup = styled(Box)(() => ({
  position: 'relative',
  marginBottom: '1rem',
  '& .input-icon': {
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 18,
    height: 18,
    color: '#9bb8cc',
    pointerEvents: 'none',
    zIndex: 1,
  },
}))

export const Input = styled(InputBase)(({ theme }) => ({
  width: '100%',
  '& .MuiInputBase-input': {
    borderRadius: 12,
    position: 'relative',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    border: '1.5px solid #dde8ef',
    fontSize: '0.95rem',
    width: '100%',
    padding: '14px 14px 14px 44px',
    transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    color: '#1a3a52',
    '&::placeholder': {
      color: '#a3bfcf',
      opacity: 1,
    },
    '&:focus': {
      borderColor: '#1a96b8',
      boxShadow: '0 0 0 3px rgba(26, 150, 184, 0.12)',
      backgroundColor: '#fff',
    },
  },
}))

/* ── Buttons ── */
export const PrimaryButton = {
  width: '100%',
  padding: '14px',
  borderRadius: '12px',
  fontSize: '0.95rem',
  fontWeight: 600,
  fontFamily: "'Inter', sans-serif",
  background: 'linear-gradient(135deg, #1a96b8 0%, #0d7a9a 100%)',
  color: 'white',
  marginBottom: '0.75rem',
  boxShadow: '0 4px 15px rgba(26, 150, 184, 0.3)',
  textTransform: 'none' as const,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 25px rgba(26, 150, 184, 0.4)',
    background: 'linear-gradient(135deg, #1a96b8 0%, #0d7a9a 100%)',
  },
  '&.Mui-disabled': {
    background: 'linear-gradient(135deg, #b0d4de 0%, #a0c4ce 100%)',
    color: 'rgba(255,255,255,0.7)',
  },
}

export const SecondaryButton = {
  width: '100%',
  padding: '14px',
  borderRadius: '12px',
  fontSize: '0.95rem',
  fontWeight: 600,
  fontFamily: "'Inter', sans-serif",
  background: 'rgba(13, 59, 102, 0.06)',
  color: '#0d3b66',
  border: '1.5px solid rgba(13, 59, 102, 0.12)',
  textTransform: 'none' as const,
  '&:hover': {
    background: 'rgba(13, 59, 102, 0.1)',
    transform: 'translateY(-1px)',
  },
  '&.Mui-disabled': {
    background: 'rgba(13, 59, 102, 0.03)',
    color: 'rgba(13, 59, 102, 0.3)',
    borderColor: 'rgba(13, 59, 102, 0.06)',
  },
}

/* ── Right Panel ── */
export const DecorativePanel = styled('div')(() => ({
  flex: 1,
  position: 'relative',
  background: 'linear-gradient(160deg, #0a2540 0%, #0d3b66 30%, #115e6e 65%, #1a96b8 100%)',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '@media (max-width: 900px)': {
    display: 'none',
  },
}))

export const Particle = styled('div')<{ index: number }>(({ index }) => {
  const configs = [
    { width: 300, height: 300, top: -80, right: -60, delay: '0s', bg: 'rgba(26, 150, 184, 0.15)' },
    { width: 200, height: 200, bottom: -40, left: -40, delay: '-5s', bg: 'rgba(255, 255, 255, 0.05)' },
    { width: 150, height: 150, top: '40%', left: '20%', delay: '-10s', bg: 'rgba(26, 150, 184, 0.08)' },
    { width: 80, height: 80, top: '20%', right: '30%', delay: '-15s', bg: 'rgba(255, 255, 255, 0.06)' },
  ]
  const c = configs[index] || configs[0]
  return {
    position: 'absolute' as const,
    borderRadius: '50%',
    background: c.bg,
    width: c.width,
    height: c.height,
    ...(c.top !== undefined && { top: c.top }),
    ...(c.right !== undefined && { right: c.right }),
    ...(c.bottom !== undefined && { bottom: c.bottom }),
    ...(c.left !== undefined && { left: c.left }),
    animation: `${float} 20s infinite ease-in-out`,
    animationDelay: c.delay,
  }
})

export const Hexagon = styled('div')<{ index: number }>(({ index }) => {
  const configs = [
    { top: '8%', left: '10%', delay: '0s', size: 80 },
    { top: '25%', right: '15%', delay: '-2s', size: 60 },
    { bottom: '20%', left: '25%', delay: '-4s', size: 50 },
    { top: '55%', right: '8%', delay: '-6s', size: 70 },
    { bottom: '8%', right: '30%', delay: '-3s', size: 45 },
  ]
  const c = configs[index] || configs[0]
  return {
    position: 'absolute' as const,
    width: c.size,
    height: c.size * 1.15,
    clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
    border: '1px solid rgba(26, 150, 184, 0.5)',
    opacity: 0.12,
    ...(c.top !== undefined && { top: c.top }),
    ...(c.right !== undefined && { right: c.right }),
    ...(c.bottom !== undefined && { bottom: c.bottom }),
    ...(c.left !== undefined && { left: c.left }),
    animation: `${hexPulse} 8s infinite ease-in-out`,
    animationDelay: c.delay,
  }
})

export const PulseLine = styled('div')(() => ({
  position: 'absolute',
  bottom: '35%',
  left: 0,
  width: '200%',
  height: 60,
  opacity: 0.15,
  animation: `${pulseSweep} 6s infinite linear`,
  '& svg': {
    width: '100%',
    height: '100%',
  },
}))

export const CircuitLine = styled('div')<{ index: number }>(({ index }) => {
  const configs = [
    { top: '30%', left: 0, width: '60%', delay: '0s' },
    { top: '50%', right: 0, width: '40%', delay: '-3s' },
    { top: '70%', left: '10%', width: '50%', delay: '-6s' },
  ]
  const c = configs[index] || configs[0]
  return {
    position: 'absolute' as const,
    background: 'linear-gradient(90deg, transparent, rgba(26, 150, 184, 0.8), transparent)',
    height: 1,
    opacity: 0.06,
    ...(c.top !== undefined && { top: c.top }),
    ...(c.left !== undefined && { left: c.left }),
    ...(c.right !== undefined && { right: c.right }),
    width: c.width,
    animation: `${hexPulse} 8s infinite linear`,
    animationDelay: c.delay,
  }
})

export const CircuitDot = styled('div')<{ index: number }>(({ index }) => {
  const configs = [
    { top: '30%', left: '60%', delay: '0s' },
    { top: '50%', right: '60%', delay: '-1s' },
    { top: '70%', left: '58%', delay: '-2s' },
  ]
  const c = configs[index] || configs[0]
  return {
    position: 'absolute' as const,
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: 'rgba(26, 150, 184, 0.5)',
    opacity: 0.06,
    ...(c.top !== undefined && { top: c.top }),
    ...(c.left !== undefined && { left: c.left }),
    ...(c.right !== undefined && { right: c.right }),
    animation: `${dotPulse} 3s infinite ease-in-out`,
    animationDelay: c.delay,
  }
})

export const DnaContainer = styled('div')(() => ({
  position: 'absolute',
  right: '12%',
  top: '10%',
  height: '80%',
  width: 40,
  opacity: 0.1,
}))

export const DnaDot = styled('div')<{ index: number }>(({ index }) => {
  const isOdd = index % 2 === 0
  const row = Math.floor(index / 2)
  return {
    position: 'absolute' as const,
    width: 5,
    height: 5,
    background: '#1a96b8',
    borderRadius: '50%',
    top: `${row * 10}%`,
    ...(isOdd ? { left: 0 } : { right: 0 }),
    animation: `${dnaSpin} 6s infinite ease-in-out`,
    animationDelay: `${-row * 0.6}s`,
    ...((!isOdd) && { animationDirection: 'reverse' }),
  }
})

export const RightContent = styled('div')(() => ({
  position: 'relative',
  zIndex: 2,
  textAlign: 'center',
  color: 'white',
  padding: '2rem',
  '& h3': {
    fontSize: '1.6rem',
    fontWeight: 300,
    lineHeight: 1.5,
    marginBottom: '1rem',
    opacity: 0.9,
  },
  '& strong': {
    fontWeight: 600,
    background: 'linear-gradient(90deg, #5fd4e8, #a8e6f0)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  '& p': {
    fontSize: '0.9rem',
    opacity: 0.5,
    fontWeight: 300,
    letterSpacing: 1,
  },
}))

export const FeaturePills = styled('div')(() => ({
  display: 'flex',
  gap: 10,
  justifyContent: 'center',
  marginTop: '2rem',
  flexWrap: 'wrap' as const,
}))

export const Pill = styled('span')(() => ({
  padding: '8px 18px',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  borderRadius: 50,
  fontSize: '0.75rem',
  color: 'rgba(255, 255, 255, 0.6)',
  backdropFilter: 'blur(8px)',
  background: 'rgba(255, 255, 255, 0.05)',
  transition: 'all 0.3s',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.1)',
    borderColor: 'rgba(255, 255, 255, 0.3)',
    color: 'rgba(255, 255, 255, 0.9)',
  },
}))
